import { ErrorHandler, Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { environment } from '@environments/environment';
import { captureException } from '@sentry/angular';

@Injectable()
export class SentryErrorHandler implements ErrorHandler {
  public handleError(error: any): void {
    if (!environment.production) {
      console.group('%c🚨 Application Error', 'color: red; font-size: 16px; font-weight: bold');
      console.error('Error object:', error);

      if (error?.message) {
        console.error('%cMessage:', 'font-weight: bold', error.message);
      }

      if (error?.stack) {
        console.error('%cStack trace:', 'font-weight: bold');
        console.error(error.stack);
      }

      // Additional debugging for component errors
      if (error?.message?.includes('ɵcmp')) {
        console.error('%c⚠️ This appears to be a component definition error.', 'color: orange; font-weight: bold');
        console.error('Check that all components used in templates are properly imported and defined.');
      }

      console.groupEnd();
    }

    const exception = error?.error || error?.message || error?.originalError || error || '';
    let errorReason = 'Unknown reason';
    const chunkFailedMessage = /Loading chunk .+ failed|Failed to fetch dynamically imported module/;

    if (chunkFailedMessage.test(exception)) {
      this.reloadPage();

      return;
    }

    if (error && error instanceof HttpErrorResponse) {
      // Handle the HTTP errors without logging the error to Sentry
      return;
    } else if (error) {
      // Handle all errors and set as much information as possible in tags
      if (error.name === 'EmptyError') {
        errorReason = 'No data or empty array returned';
      } else if (error.name === '<unknown>' || error.title === 'unknown') {
        errorReason = `The <unknown> title usually means that there was no error message when the error was
                       generated or is not caught and handled by the error handling system`;
      }

      this.captureSentryException(error, errorReason);
    }
  }

  public reloadPage(): void {
    window.location.reload();
  }

  public captureSentryException(error: any, errorReason: string): void {
    captureException(error, {
      tags: {
        headers: error.headers,
        message: error.message,
        name: error.name,
        reason: errorReason,
      },
    });
  }
}
